import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, CircularProgress } from '@mui/material';
import { apiStore } from '../../api/ApiFactory';
import { FileInfo } from '../../models/Media';

interface MediaFile {
    name: string;
    type: string;
    size: number;
}

interface MediaPreviewModalProps {
    open: boolean;
    onClose: () => void;
    fileInfo: FileInfo;
}

const MediaPreviewModal: React.FC<MediaPreviewModalProps> = ({ open, onClose, fileInfo }) => {
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const { file, path, mimeType } = fileInfo;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    useEffect(() => {
        const fetchMedia = async () => {
            if (!file && path) {
                const queryParams = { path };
                try {
                    setIsLoading(true);
                    const response = await apiStore.warrantyclaimApi.downloadWarrantyClaimMedia(queryParams, mimeType || 'image');
                    if (response?.url) {
                        setObjectUrl(response.url);
                    }
                } catch (error) {
                    console.error('Error fetching media:', error);
                }
                finally { setIsLoading(false); }
            }
        };

        if (file) {
            setIsLoading(true);
            const url = URL.createObjectURL(file as Blob);
            setObjectUrl(url);
            setIsLoading(false);
            return () => URL.revokeObjectURL(url);
        } else {            
            fetchMedia();
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
                setIsLoading(false);
            }
        };
    }, [file, path, mimeType]);

    const renderContent = () => {
        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (!objectUrl) return null;

        const fileName = file ? path || 'Preview' : path?.split('/').pop() || 'Preview';
        const fileType = mimeType || 'image/png';

        if (fileType.startsWith('image/')) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '80vh',   
                        width: '80vw',    
                        maxHeight: '90vh',
                        maxWidth: '90vw',  
                        overflow: 'hidden', 
                        position: 'relative',
                    }}
                >
                    {objectUrl ? <img src={objectUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <p>Loading...</p>}
                </Box>
            );
        } else if (fileType === 'application/pdf') {
            return (
                <iframe
                    src={objectUrl}
                    title={fileName}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                />
            );
        } else if (fileType.startsWith('video/')) {
            return (
                <video controls style={{ width: '100%', height: '100%' }}>
                    <source src={objectUrl} type={fileType} />
                    Your browser does not support the video tag.
                </video>
            );
        } else if (fileType === 'application/msword' || fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return (
                <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(objectUrl)}&embedded=true`}
                    title={fileName}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                />
            );
        }

        return <Typography variant="body2">File type not supported for preview.</Typography>;
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    padding: 2,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    width: '80%',
                    height: '90%',
                    margin: '20px auto',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div style={{ flexGrow: 1 }}>
                    {renderContent()}
                </div>
                <Typography
                    onClick={onClose}
                    sx={{ alignSelf: 'flex-end', marginTop: 2, cursor: 'pointer', color: 'primary.main' }}
                >
                    Close
                </Typography>
            </Box>
        </Modal>
    );
};

export default MediaPreviewModal;
